import { Link } from "@inertiajs/react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import MilestonesCard from "@/components/schools/MilestonesCard";
import PrizeTrackerCard from "@/components/schools/PrizeTrackerCard";
import ClassroomTodayCard from "@/components/schools/classroom/ClassroomTodayCard";
import { CAMPAIGN_ACTIVE, CLASSROOM_MILESTONES, PRIZES_FOR_TRACKER } from "@/help/fixtures";

const ROOM_12 = {
  name: "Room 12",
  grade: "Grade 5",
  teachers: [],
  student_count: 24,
  total_minutes_logged: 3420,
  total_raised_cents: 48500,
  minute_goal: 6000,
  money_goal_cents: null,
};

export default function TeacherClassroomDashboard() {
  return (
    <>
      <p>
        Your classroom dashboard is the only page you really need. Your class comes first, with everything you do day to day, and the whole school's progress sits lower down.
      </p>

      <h2>Your class</h2>
      <p>
        At the top: how many minutes your class has read, how much it's raised, and your class goal if you've set one. The <strong>Log minutes</strong> button is right there while reading is open. Before reading starts, and after it ends, the card says what's happening instead — when logging opens, or how long you have left to log final minutes.
      </p>
      <Screenshot caption="The top of the dashboard">
        <ClassroomTodayCard
          classroom={ROOM_12}
          campaign={{ ...CAMPAIGN_ACTIVE, accepting_minutes: true }}
          phase="reading"
          log_minutes_path="#"
          goals_path="#"
        />
      </Screenshot>
      <p>
        No class goal yet? The card offers to set one. See <Link href="/help/articles/setting-classroom-goals-and-milestones">Classroom goals and rewards</Link>.
      </p>

      <h2>Leaderboard and recent activity</h2>
      <p>
        Your students, ranked by reading minutes (switch to money raised with the toggle). Click a student to open their page, for instance to log minutes just for them.
      </p>
      <p>
        Next to it, every reading log and donation, newest first. Reading logs have <strong>Edit</strong> and <strong>Undo</strong> buttons if you need to fix something — see <Link href="/help/articles/undoing-minute-batches">Undoing a reading log</Link>.
      </p>

      <h2>Prizes to hand out</h2>
      <p>
        When students earn prizes your school admin set up (like "read 500 minutes, get a fidget spinner"), a note near the top says how many are waiting, and they're listed in the prize tracker. See <Link href="/help/articles/handing-out-student-prizes">Handing out student prizes</Link>.
      </p>
      <Screenshot caption="The prize tracker">
        <PrizeTrackerCard prizes={PRIZES_FOR_TRACKER} />
      </Screenshot>
      <p>
        A <strong>Top student</strong> prize shows who's leading. If it runs across a division or the whole school and the leader is in another class, you won't see their name. You'll see your class's top student instead, with their place and how far behind first they are.
      </p>

      <h2>Classroom rewards</h2>
      <p>The rewards just for your class, with how close you are to each one.</p>
      <Screenshot caption="The classroom rewards card">
        <MilestonesCard milestones={CLASSROOM_MILESTONES} title="Classroom rewards" />
      </Screenshot>

      <h2>Across the school</h2>
      <p>
        Lower down: the whole school's totals, its school-wide rewards, and the top students and classrooms. Once donations open, there's also a card for sharing the school's donation page.
      </p>

      <Callout variant="tip" title="Add this page to your phone's home screen">
        <p>You'll be one tap from logging the day's reading.</p>
      </Callout>

      <p>
        If you teach more than one classroom, switch between them with the room buttons at the top of the page.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/logging-classroom-minutes">Logging reading minutes</Link>.</li>
        <li><Link href="/help/articles/handing-out-student-prizes">Handing out student prizes</Link>.</li>
        <li><Link href="/help/articles/setting-classroom-goals-and-milestones">Classroom goals and rewards</Link>.</li>
      </ul>
    </>
  );
}
